import React, { useEffect, useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  ArrowLeft,
  MapPin,
  Clock,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Info,
  Tag,
  Eye,
  EyeOff,
  Edit,
  X,
} from 'lucide-react'
import { adminVendorsService, adminVendorCategoriesService } from '../services'
import api from '../services/api'
import { formatPriceDisplay } from '../utils/formatPrice'
import ProfileAvatar from '../components/ProfileAvatar'
import './VendorDetail.css'

const DAY_LABELS = {
  sun: 'Sunday',
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
}

const WEEK_ORDER = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

const IN_COLOR = '#02c0a7'
const EXTERNAL_COLOR = '#f59e0b'
const MAX_VENDOR_PLACE_PHOTOS = 3

/** At least one offering with non-empty name and a finite price ≥ 0 (dashboard + API alignment). */
const getOfferingsValidationMessage = (offerings) => {
  if (!Array.isArray(offerings) || offerings.length === 0) {
    return 'Add at least one offering with a name and price.'
  }
  const hasValid = offerings.some((o) => {
    const name = (o?.name ?? '').toString().trim()
    const p = parseFloat(String(o?.price ?? '').replace(/,/g, ''))
    return name.length > 0 && !Number.isNaN(p) && p >= 0
  })
  if (!hasValid) return 'Each offering needs a name and a valid price (0 or greater).'
  return null
}

const pad2 = (n) => String(n).padStart(2, '0')

const dateKey = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

const parseDateMaybe = (value) => {
  if (!value) return null
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d
}

const formatBookingTime = (startValue, endValue) => {
  const startDt = parseDateMaybe(startValue)
  if (!startDt) return '--:--'
  const start = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(startDt)

  const endDt = parseDateMaybe(endValue)
  if (!endValue || !endDt) return start

  const end = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(endDt)

  return `${start} - ${end}`
}

const formatTime = (value) => {
  if (!value || typeof value !== 'string') return '--:--'
  const parts = value.split(':')
  if (parts.length < 2) return value
  const h = Number(parts[0])
  const m = Number(parts[1])
  if (Number.isNaN(h) || Number.isNaN(m)) return value
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h
  const period = h < 12 ? 'AM' : 'PM'
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`
}

const getCategories = (vendor) => {
  const raw =
    vendor?.activityCategories ??
    vendor?.activity_categories ??
    vendor?.categories ??
    vendor?.category ??
    vendor?.partnerCategory

  if (Array.isArray(raw)) {
    return raw.map(c => typeof c === 'string' ? c.trim() : (c?.name || c?.title || '')).filter(Boolean)
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    return [raw.trim()]
  }
  return []
}

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.onload = () => resolve(String(reader.result || ''))
  reader.onerror = () => reject(new Error('Failed to read image file'))
  reader.readAsDataURL(file)
})

const compressImageToDataUrl = (file) => new Promise((resolve, reject) => {
  const objectUrl = URL.createObjectURL(file)
  const img = new Image()

  img.onload = () => {
    try {
      const maxSide = 420
      const scale = Math.min(1, maxSide / Math.max(img.width, img.height))
      const width = Math.max(1, Math.round(img.width * scale))
      const height = Math.max(1, Math.round(img.height * scale))

      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        URL.revokeObjectURL(objectUrl)
        reject(new Error('Failed to process image'))
        return
      }

      ctx.drawImage(img, 0, 0, width, height)
      let quality = 0.78
      let compressed = canvas.toDataURL('image/jpeg', quality)

      // Try to keep payload small for JSON transport (roughly < 1.6MB base64 string)
      const maxBase64Length = 600_000
      while (compressed.length > maxBase64Length && quality > 0.45) {
        quality = Number((quality - 0.08).toFixed(2))
        compressed = canvas.toDataURL('image/jpeg', quality)
      }

      URL.revokeObjectURL(objectUrl)
      resolve(compressed)
    } catch (err) {
      URL.revokeObjectURL(objectUrl)
      reject(err)
    }
  }

  img.onerror = () => {
    URL.revokeObjectURL(objectUrl)
    reject(new Error('Invalid image file'))
  }

  img.src = objectUrl
})

const VendorDetail = () => {
  const { id } = useParams()
  const [partner, setPartner] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [savingEdit, setSavingEdit] = useState(false)
  const [partnerCategories, setPartnerCategories] = useState([])
  const [allowedLocations, setAllowedLocations] = useState([])

  const [offerings, setOfferings] = useState([])
  const [vendorBookings, setVendorBookings] = useState([])

  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), 1)
  })
  const [selectedDate, setSelectedDate] = useState(() => new Date())

  const loadPartner = async () => {
    if (!id) return
    try {
      setLoading(true)
      setError(null)
      const [data, categories, locationsRes, offeringsRes, bookingsRes] = await Promise.all([
        adminVendorsService.getById(id),
        adminVendorCategoriesService.list(),
        api.get('/admin/vendors/locations').catch(() => ({ items: [] })),
        api.get(`/admin/vendors/${id}/offerings`).catch(() => ({ items: [] })),
        api.get(`/admin/vendors/${id}/bookings`).catch(() => ({ items: [] }))
      ])
      setPartner(data)
      setPartnerCategories(categories || [])
      setAllowedLocations(locationsRes?.items || [])
      setOfferings(offeringsRes?.items || [])
      setVendorBookings(bookingsRes?.items || [])
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load partner details')
    } finally {
      setLoading(false)
    }
  }

  const refreshBookings = async () => {
    if (!id) return
    try {
      const bookingsRes = await api.get(`/admin/vendors/${id}/bookings`)
      setVendorBookings(bookingsRes?.items || [])
    } catch (e) {
      console.error('Failed to refresh bookings:', e)
    }
  }

  useEffect(() => {
    loadPartner()
  }, [id])

  const displayName =
    partner?.name || partner?.businessName || partner?.userName || 'Partner'

  const logoUrl = partner?.profilePhotoUrl || null

  const categories = useMemo(() => getCategories(partner), [partner])
  const primaryPartnerCategory = categories[0] || 'No partner category assigned'

  const operatingHoursRows = useMemo(() => {
    const source =
      partner?.operatingHours && typeof partner.operatingHours === 'object'
        ? partner.operatingHours
        : {}

    return WEEK_ORDER.map((key) => {
      const day = source[key] || {}
      const isOpen = !!day?.isOpen
      const open = day?.openTime
      const close = day?.closeTime
      return {
        day: DAY_LABELS[key],
        hours: isOpen ? `${formatTime(open)} - ${formatTime(close)}` : 'Closed',
      }
    })
  }, [partner])

  const dotsByDay = useMemo(() => {
    const map = {}
    if (!Array.isArray(vendorBookings) || vendorBookings.length === 0) return map

    const ym = { year: currentMonth.getFullYear(), month: currentMonth.getMonth() }
    for (const b of vendorBookings) {
      const dt = parseDateMaybe(b?.scheduledStart ?? b?.scheduled_start)
      if (!dt) continue
      if (dt.getFullYear() !== ym.year || dt.getMonth() !== ym.month) continue

      const k = dateKey(dt)
      const type = (b?.scheduleType ?? b?.schedule_type ?? 'in') === 'external'
        ? 'external'
        : 'in'

      if (!map[k]) map[k] = []
      map[k].push(type)
    }
    return map
  }, [vendorBookings, currentMonth])

  const selectedDayKey = dateKey(selectedDate)
  const bookingsForSelectedDay = useMemo(() => {
    if (!selectedDayKey) return []
    return (vendorBookings || [])
      .filter((b) => {
        const dt = parseDateMaybe(b?.scheduledStart ?? b?.scheduled_start)
        return dt ? dateKey(dt) === selectedDayKey : false
      })
      .slice()
      .sort((a, b) => {
        const ad = parseDateMaybe(a?.scheduledStart ?? a?.scheduled_start)?.getTime() ?? 0
        const bd = parseDateMaybe(b?.scheduledStart ?? b?.scheduled_start)?.getTime() ?? 0
        return ad - bd
      })
  }, [vendorBookings, selectedDayKey])

  const monthYearLabel = useMemo(() => {
    return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(currentMonth)
  }, [currentMonth])

  /** Partner place / gallery images only (max 3); logo stays in the header. */
  const galleryImages = useMemo(() => {
    const raw = partner?.placePhotoUrls ?? partner?.place_photo_urls
    if (!Array.isArray(raw)) return []
    const seen = new Set()
    return raw
      .filter((value) => typeof value === 'string' && value.trim())
      .filter((value) => {
        const u = value.trim()
        if (seen.has(u)) return false
        seen.add(u)
        return true
      })
      .slice(0, MAX_VENDOR_PLACE_PHOTOS)
  }, [partner])



  const handleUpdatePartner = async (formData) => {
    if (!partner?.id || savingEdit) return

    try {
      setSavingEdit(true)
      setError(null)
      const payload = {
        name: formData.name,
        profilePhotoUrl: formData.profilePhotoUrl,
        placePhotoUrls: Array.isArray(formData.placePhotoUrls)
          ? formData.placePhotoUrls.filter((u) => typeof u === 'string' && u.trim()).slice(0, MAX_VENDOR_PLACE_PHOTOS)
          : [],
        location: formData.location,
        locationLink: formData.locationLink?.trim() || null,
        contact: formData.contact,
        email: formData.email,
        description: formData.description,
        capacity: formData.capacity,
      }

      if (formData.partnerCategory) {
        payload.partnerCategory = formData.partnerCategory;
      }

      await adminVendorsService.update(partner.id, payload)
      
      await api.put(`/admin/vendors/${partner.id}/offerings`, {
        offerings: formData.offerings,
      })

      await loadPartner()
      setShowEditModal(false)
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to update partner')
    } finally {
      setSavingEdit(false)
    }
  }

  if (loading) {
    return (
      <div className="vendor-detail-page">
        <Link to="/vendors" className="back-link">
          <ArrowLeft size={16} /> Back to Partners
        </Link>
        <div className="card">Loading partner details...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="vendor-detail-page">
        <Link to="/vendors" className="back-link">
          <ArrowLeft size={16} /> Back to Partners
        </Link>
        <div className="card">{error}</div>
      </div>
    )
  }

  if (!partner) {
    return (
      <div className="vendor-detail-page">
        <Link to="/vendors" className="back-link">
          <ArrowLeft size={16} /> Back to Partners
        </Link>
        <div className="card">Partner not found</div>
      </div>
    )
  }

  return (
    <div className="vendor-detail-page">
      <div className="vendor-detail-top-bar">
        <Link to="/vendors" className="back-link">
          <ArrowLeft size={16} /> Back to Partners
        </Link>
        <div className="vendor-detail-actions">
          <button className="btn-secondary" onClick={() => setShowEditModal(true)}>
            <Edit size={16} /> Edit Partner
          </button>
        </div>
      </div>

      <div className="vendor-header-card">
        <div className="vendor-header-left">
          <div className="vendor-large-logo">
            <ProfileAvatar src={logoUrl} alt={displayName} />
          </div>
          <div className="vendor-title-section">
            <div className="header-titles">
              <h1>{displayName}</h1>
              <p style={{ color: 'var(--text-secondary)', marginTop: '0.25rem', marginBottom: '1rem' }}>
                {partner.description || 'No description provided.'}
              </p>
            </div>
            <div className="vendor-meta">
              <div className="meta-item">
                <Tag size={16} />
                <span>{primaryPartnerCategory}</span>
              </div>
              <div className="meta-item">
                <MapPin size={16} />
                <span>{partner.location || '—'}</span>
              </div>
            </div>
          </div>
        </div>
        <div className={`status-badge ${String(partner.status || '').toLowerCase()}`}>
          {partner.status || 'Shown'}
        </div>
      </div>

      <div className="detail-content-grid">
        <div className="info-section">
          <div className="info-grid">
            <div className="info-card">
              <h3>
                <Info size={18} /> Basic Info
              </h3>
              <div className="info-list">
                <div className="info-item">
                  <span className="info-label">Contact</span>
                  <span className="info-value">{partner.contactPhone || partner.userPhone || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Email</span>
                  <span className="info-value">{partner.contactEmail || partner.userEmail || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Reservable</span>
                  <span className="info-value">{partner.isReservable ? 'Yes' : 'No'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Directions link</span>
                  <span className="info-value" style={{ wordBreak: 'break-all' }}>
                    {partner.locationLink || partner.location_link || '—'}
                  </span>
                </div>
              </div>
            </div>

            <div className="info-card">
              <div className="offering-header">
                <h3><Tag size={18} /> Offerings & Prices</h3>
              </div>
              {offerings.length === 0 ? (
                <p className="info-label">No offerings added yet.</p>
              ) : (
                <div className="offering-list">
                  {offerings.map((off) => (
                    <div key={off.id} className="offering-item">
                      <div className="offering-info">
                        <span className="offering-name">{off.name}</span>
                        {off.description && <span className="offering-desc">{off.description}</span>}
                      </div>
                      <div className="offering-price-tag">{formatPriceDisplay(off.price)} EGP</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>

        <div className="secondary-section">
          <div className="info-card hours-card">
            <h3>
              <Clock size={18} /> Weekly Schedule
            </h3>
            {operatingHoursRows.map((item) => (
              <div key={item.day} className="hour-row">
                <span className="info-label">{item.day}</span>
                <span className="info-value">{item.hours}</span>
              </div>
            ))}
          </div>

          <div className="info-card hours-card">
            <div className="calendar-header-row">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <Clock size={18} /> Schedule Calendar
              </h3>
              <button
                type="button"
                className="refresh-btn"
                onClick={refreshBookings}
                title="Refresh bookings"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 4v6h-6" />
                  <path d="M1 20v-6h6" />
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
                  <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
                </svg>
              </button>
            </div>

            <div className="booking-calendar">
              <div className="booking-calendar-header">
                <button
                  type="button"
                  className="calendar-nav-btn"
                  onClick={() => {
                    setCurrentMonth((p) => new Date(p.getFullYear(), p.getMonth() - 1, 1))
                  }}
                  aria-label="Previous month"
                >
                  <ChevronLeft size={20} />
                </button>
                <div className="calendar-month-label">
                  <span className="calendar-month-text">{monthYearLabel}</span>
                </div>
                <button
                  type="button"
                  className="calendar-nav-btn"
                  onClick={() => {
                    setCurrentMonth((p) => new Date(p.getFullYear(), p.getMonth() + 1, 1))
                  }}
                  aria-label="Next month"
                >
                  <ChevronRight size={20} />
                </button>
              </div>

              <div className="calendar-weekdays">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d) => (
                  <div key={d} className="calendar-weekday">
                    {d}
                  </div>
                ))}
              </div>

              {(() => {
                const year = currentMonth.getFullYear()
                const month = currentMonth.getMonth()
                const daysInMonth = new Date(year, month + 1, 0).getDate()
                const leadingEmpty = new Date(year, month, 1).getDay() // Sun=0
                const totalCells = leadingEmpty + daysInMonth
                const rows = Math.ceil(totalCells / 7)
                const cellCount = rows * 7

                const cells = []
                for (let idx = 0; idx < cellCount; idx++) {
                  const dayNum = idx - leadingEmpty + 1
                  const isEmpty = idx < leadingEmpty || dayNum > daysInMonth
                  if (isEmpty) {
                    cells.push(
                      <div key={`e-${idx}`} className="calendar-day-cell calendar-day-cell--empty" />
                    )
                    continue
                  }

                  const date = new Date(year, month, dayNum)
                  const k = dateKey(date)
                  const isSelected = k === selectedDayKey
                  const dots = (dotsByDay[k] || []).slice(0, 3)

                  cells.push(
                    <button
                      type="button"
                      key={`d-${idx}`}
                      className={`calendar-day-cell ${isSelected ? 'calendar-day-cell--selected' : ''}`}
                      onClick={() => setSelectedDate(date)}
                    >
                      <div className="calendar-day-number">{dayNum}</div>
                      {dots.length > 0 && (
                        <div className="calendar-dots">
                          {dots.map((t, dotIdx) => (
                            <span
                              key={`${k}-${dotIdx}`}
                              className={`calendar-dot calendar-dot--${t}`}
                              style={{ backgroundColor: t === 'external' ? EXTERNAL_COLOR : IN_COLOR }}
                            />
                          ))}
                        </div>
                      )}
                    </button>
                  )
                }

                return <div className="calendar-grid">{cells}</div>
              })()}

              <div className="calendar-legend">
                <div className="legend-item">
                  <span className="legend-dot" style={{ backgroundColor: IN_COLOR }} />
                  <span>IN</span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot" style={{ backgroundColor: EXTERNAL_COLOR }} />
                  <span>External</span>
                </div>
              </div>

              <div className="booking-day-section">
                <div className="booking-day-title">
                  Bookings on{' '}
                  {selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
                {bookingsForSelectedDay.length === 0 ? (
                  <div className="booking-empty">No bookings on this day.</div>
                ) : (
                  <div className="booking-list">
                    {bookingsForSelectedDay.map((b) => {
                      const type = (b?.scheduleType ?? 'in') === 'external' ? 'external' : 'in'
                      const typeColor = type === 'external' ? EXTERNAL_COLOR : IN_COLOR
                      const timeStr = formatBookingTime(b?.scheduledStart ?? b?.scheduled_start, b?.scheduledEnd ?? b?.scheduled_end)
                      const title = b?.activityName ?? b?.activity_name ?? 'Activity'
                      const status = b?.bookingStatus ?? b?.booking_status ?? 'confirmed'
                      const guestName = b?.guestName ?? b?.guest_name ?? ''

                      return (
                        <div key={b.id} className="booking-card">
                          <div className="booking-card-top">
                            <div className="booking-title">{title}</div>
                            <div className="booking-type-pill" style={{ backgroundColor: `${typeColor}22`, color: typeColor }}>
                              {type === 'external' ? 'External' : 'IN'}
                            </div>
                          </div>
                          <div className="booking-card-meta">
                            <span className="booking-time">{timeStr}</span>
                            {guestName ? <span className="booking-guest">Guest: {guestName}</span> : null}
                          </div>
                          <div className="booking-status-row">
                            <span className="booking-status-label">Status</span>
                            <span className="booking-status-value">{String(status).toUpperCase()}</span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="info-card gallery-card">
            <h3>
              <ImageIcon size={18} /> Place photos
            </h3>
            <p className="info-label" style={{ marginTop: '-0.25rem', marginBottom: '0.75rem' }}>
              Up to {MAX_VENDOR_PLACE_PHOTOS} images (editable in Edit Partner).
            </p>
            {galleryImages.length === 0 ? (
              <p className="info-label">No images uploaded.</p>
            ) : (
              <div className="image-gallery">
                {galleryImages.map((img, index) => (
                  <div key={`${img}-${index}`} className="gallery-item">
                    <img src={img} alt={`Partner ${index + 1}`} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showEditModal && (
        <VendorEditModal
          vendor={partner}
          initialOfferings={offerings}
          partnerCategories={partnerCategories}
          allowedLocations={allowedLocations}
          saving={savingEdit}
          onClose={() => setShowEditModal(false)}
          onSave={handleUpdatePartner}
        />
      )}
    </div>
  )
}

const VendorEditModal = ({ vendor, initialOfferings, partnerCategories, allowedLocations, saving, onClose, onSave }) => {
  const getInitialPartnerCategory = (value) => {
    const raw =
      value?.activityCategories ??
      value?.activity_categories ??
      value?.categories ??
      value?.category ??
      value?.partnerCategory

    if (Array.isArray(raw) && raw.length > 0) {
      const first = raw[0];
      if (typeof first === 'string' && first.trim()) return first.trim();
      if (first && typeof first === 'object') return (first.name || first.title || '').trim();
    }
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
    return '';
  }

  const buildInitialForm = (v, offs) => ({
    name: v.name || v.businessName || '',
    profilePhotoUrl: v.profilePhotoUrl || '',
    placePhotoUrls: (() => {
      const raw = v.placePhotoUrls ?? v.place_photo_urls
      if (!Array.isArray(raw)) return []
      return raw
        .filter((x) => typeof x === 'string' && x.trim())
        .slice(0, MAX_VENDOR_PLACE_PHOTOS)
    })(),
    location: v.location || '',
    locationLink: v.locationLink || v.location_link || '',
    contact: v.contactPhone || v.userPhone || '',
    email: v.contactEmail || v.userEmail || '',
    description: v.description || '',
    capacity: v.capacity ?? '',
    status: v.status || 'Shown',
    partnerCategory: getInitialPartnerCategory(v),
    offerings: offs ? [...offs] : []
  })

  const [formData, setFormData] = useState(() => buildInitialForm(vendor, initialOfferings))
  const [uploadError, setUploadError] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [placeUploadError, setPlaceUploadError] = useState(null)
  const [uploadingPlace, setUploadingPlace] = useState(false)
  const [offeringsFormError, setOfferingsFormError] = useState(null)
  const fileInputRef = React.useRef(null)
  const placeFileInputRef = React.useRef(null)

  // Re-initialise form if the vendor data changes (e.g. after a reload)
  React.useEffect(() => {
    setFormData(buildInitialForm(vendor, initialOfferings))
    setUploadError(null)
    setPlaceUploadError(null)
    setOfferingsFormError(null)
  }, [vendor, initialOfferings])

  const handlePlacePhotoFileChange = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    if ((formData.placePhotoUrls || []).length >= MAX_VENDOR_PLACE_PHOTOS) {
      setPlaceUploadError(`You can upload at most ${MAX_VENDOR_PLACE_PHOTOS} place photos.`)
      event.target.value = ''
      return
    }

    try {
      setPlaceUploadError(null)
      setUploadingPlace(true)
      let dataUrl = await compressImageToDataUrl(file)
      if (!dataUrl || !dataUrl.startsWith('data:image/')) {
        dataUrl = await fileToDataUrl(file)
      }

      if (dataUrl.length > 750_000) {
        throw new Error('Image is still too large. Please choose a smaller image (< 1 MB).')
      }

      setFormData((prev) => {
        const next = [...(prev.placePhotoUrls || [])].filter((u) => typeof u === 'string' && u.trim())
        if (next.length >= MAX_VENDOR_PLACE_PHOTOS) return prev
        next.push(dataUrl)
        return { ...prev, placePhotoUrls: next }
      })
    } catch (e) {
      setPlaceUploadError(e.message || 'Failed to process image')
    } finally {
      setUploadingPlace(false)
      event.target.value = ''
    }
  }

  const handleLogoFileChange = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      setUploadError(null)
      setUploading(true)
      let dataUrl = await compressImageToDataUrl(file)
      if (!dataUrl || !dataUrl.startsWith('data:image/')) {
        dataUrl = await fileToDataUrl(file)
      }

      if (dataUrl.length > 750_000) {
        throw new Error('Image is still too large. Please choose a smaller image (< 1 MB).')
      }

      setFormData((prev) => ({ ...prev, profilePhotoUrl: dataUrl }))
    } catch (e) {
      setUploadError(e.message || 'Failed to process image')
    } finally {
      setUploading(false)
      event.target.value = ''
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const msg = getOfferingsValidationMessage(formData.offerings)
    if (msg) {
      setOfferingsFormError(msg)
      return
    }
    setOfferingsFormError(null)
    onSave(formData)
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Edit Partner</h2>
          <button type="button" className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Partner Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Partner Logo</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleLogoFileChange}
                  disabled={uploading || saving}
                />

                {/* Preview + actions row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem' }}>
                  {/* Thumbnail / placeholder */}
                  <div
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: 10,
                      border: '2px dashed var(--border-color)',
                      overflow: 'hidden',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'var(--background)',
                      position: 'relative',
                    }}
                  >
                    {uploading ? (
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', textAlign: 'center', padding: '0.25rem' }}>Processing…</span>
                    ) : formData.profilePhotoUrl ? (
                      <img
                        src={formData.profilePhotoUrl}
                        alt="Logo preview"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        onError={(e) => { e.currentTarget.style.display = 'none' }}
                      />
                    ) : (
                      <ImageIcon size={24} style={{ color: 'var(--text-secondary)', opacity: 0.5 }} />
                    )}
                  </div>

                  {/* Buttons */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ fontSize: '0.8rem', padding: '0.4rem 0.85rem' }}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || saving}
                    >
                      {uploading ? 'Processing…' : formData.profilePhotoUrl ? 'Change Image' : 'Upload Image'}
                    </button>
                    {formData.profilePhotoUrl && !uploading && (
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ fontSize: '0.8rem', padding: '0.4rem 0.85rem', color: 'var(--danger)', borderColor: 'var(--danger)' }}
                        onClick={() => {
                          setFormData((prev) => ({ ...prev, profilePhotoUrl: '' }))
                          setUploadError(null)
                        }}
                        disabled={saving}
                      >
                        Remove Image
                      </button>
                    )}
                  </div>
                </div>

                {uploadError && (
                  <p style={{ color: 'var(--danger)', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                    ⚠ {uploadError}
                  </p>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Place photos (max {MAX_VENDOR_PLACE_PHOTOS})</label>
                <input
                  ref={placeFileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handlePlacePhotoFileChange}
                  disabled={uploadingPlace || saving || (formData.placePhotoUrls || []).length >= MAX_VENDOR_PLACE_PHOTOS}
                />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '0.5rem', alignItems: 'flex-start' }}>
                  {(formData.placePhotoUrls || []).map((url, idx) => (
                    <div
                      key={`${idx}-${url?.slice?.(0, 48) || idx}`}
                      style={{
                        position: 'relative',
                        width: 88,
                        height: 88,
                        borderRadius: 10,
                        border: '2px dashed var(--border-color)',
                        overflow: 'hidden',
                        flexShrink: 0,
                        background: 'var(--background)',
                      }}
                    >
                      {url ? (
                        <img
                          src={url}
                          alt={`Place ${idx + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          onError={(e) => { e.currentTarget.style.display = 'none' }}
                        />
                      ) : null}
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          padding: '0.15rem 0.35rem',
                          fontSize: '0.65rem',
                          lineHeight: 1,
                          minHeight: 'auto',
                        }}
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            placePhotoUrls: (prev.placePhotoUrls || []).filter((_, i) => i !== idx),
                          }))
                          setPlaceUploadError(null)
                        }}
                        disabled={saving}
                        title="Remove photo"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {(formData.placePhotoUrls || []).length < MAX_VENDOR_PLACE_PHOTOS && (
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ fontSize: '0.8rem', padding: '0.5rem 0.85rem', alignSelf: 'center' }}
                      onClick={() => placeFileInputRef.current?.click()}
                      disabled={uploadingPlace || saving}
                    >
                      {uploadingPlace ? 'Processing…' : 'Add photo'}
                    </button>
                  )}
                </div>
                {placeUploadError && (
                  <p style={{ color: 'var(--danger)', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                    ⚠ {placeUploadError}
                  </p>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Location</label>
                <select
                  className="form-select"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                >
                  <option value="">Select location</option>
                  {allowedLocations.map((loc) => (
                    <option key={loc} value={loc}>
                      {loc}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Directions link (Maps URL)</label>
              <input
                type="url"
                className="form-input"
                placeholder="https://maps.google.com/..."
                value={formData.locationLink}
                onChange={(e) => setFormData({ ...formData, locationLink: e.target.value })}
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.35rem' }}>
                Opened when guests tap the directions icon after booking. Use a full Google Maps or Apple Maps link.
              </p>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Contact</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.contact}
                  onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Capacity</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  className="form-input"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                />
              </div>
            </div>

            <div className="form-group" style={{ marginTop: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <label className="form-label" style={{ marginBottom: 0 }}>Offerings & Prices *</label>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      offerings: [...prev.offerings, { name: '', price: '', description: '' }],
                    }))
                    if (offeringsFormError) setOfferingsFormError(null)
                  }}
                >
                  + Add offering
                </button>
              </div>
              
              <div className="offering-editor-list">
                {offeringsFormError && (
                  <p style={{ color: 'var(--danger)', fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                    {offeringsFormError}
                  </p>
                )}
                {formData.offerings.length === 0 && (
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontStyle: 'italic', textAlign: 'center', padding: '1rem' }}>No offerings added yet.</p>
                )}
                {formData.offerings.map((off, idx) => (
                  <div key={idx} className="offering-edit-row">
                    <div className="offering-edit-inputs">
                      <input
                        type="text"
                        placeholder="Offering name (e.g. Adult Ticket)"
                        className="form-input"
                        value={off.name}
                        onChange={(e) => {
                          const newOffs = [...formData.offerings]
                          newOffs[idx].name = e.target.value
                          setFormData({ ...formData, offerings: newOffs })
                          if (offeringsFormError) setOfferingsFormError(null)
                        }}
                      />
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input
                          type="text"
                          inputMode="decimal"
                          autoComplete="off"
                          placeholder="Price (EGP)"
                          className="form-input"
                          style={{ flex: 1 }}
                          value={off.price}
                          onChange={(e) => {
                            const newOffs = [...formData.offerings]
                            newOffs[idx].price = e.target.value
                            setFormData({ ...formData, offerings: newOffs })
                            if (offeringsFormError) setOfferingsFormError(null)
                          }}
                        />
                        <input
                          type="text"
                          placeholder="Short description (optional)"
                          className="form-input"
                          style={{ flex: 2 }}
                          value={off.description || ''}
                          onChange={(e) => {
                            const newOffs = [...formData.offerings]
                            newOffs[idx].description = e.target.value
                            setFormData({ ...formData, offerings: newOffs })
                          }}
                        />
                      </div>
                    </div>
                    <button 
                      type="button" 
                      className="btn-remove-offering"
                      title="Remove offering"
                      disabled={formData.offerings.length <= 1}
                      onClick={() => {
                        if (formData.offerings.length <= 1) return
                        const newOffs = [...formData.offerings]
                        newOffs.splice(idx, 1)
                        setFormData({ ...formData, offerings: newOffs })
                        if (offeringsFormError) setOfferingsFormError(null)
                      }}
                    >
                      <X size={18} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Partner Category</label>
              <select
                className="form-select"
                value={formData.partnerCategory || ''}
                onChange={(e) => setFormData({ ...formData, partnerCategory: e.target.value })}
              >
                <option value="">Select partner category</option>
                {partnerCategories.map((cat) => (
                  <option key={cat.id} value={cat.name}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-textarea"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving || uploading || uploadingPlace}>
              {saving ? 'Saving…' : uploading || uploadingPlace ? 'Processing image…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default VendorDetail
